import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MpError } from '@mp-core/interfaces/mp-error';
import { ErrorService } from '@mp-core/services/error.service';
import { Constants } from '@mp-core/enums/constants';
import { ShowResponse } from '@mp-core/responses/show-response';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.baseUrl;

  constructor(
    private http: HttpClient,
    private router: Router,
    private errorService: ErrorService) {
  }

  private static _getToken(): string {
    return localStorage.getItem(Constants.AUTH_TOKEN);
  }

  private static _removeToken() {
    localStorage.removeItem(Constants.AUTH_TOKEN);
  }

  private static _buildParams(data: any): HttpParams {
    let params = new HttpParams();
    if (data) {
      for (const key of Object.keys(data)) {
        console.log('KEY', key);
        // Beware .append is not mutable operation
        params = params.append(key, data[key]);
      }
    }
    return params;
  }

  get<T>(endpoint: string, data?: any, useBaseUrl: boolean = true, useAuthHeaders: boolean = true): Observable<T> {
    const options = {
      headers: this._buildHeaders(useAuthHeaders),
      params: ApiService._buildParams(data),
    };
    return this.http.get<ShowResponse<T>>(`${this._buildUrl(endpoint, useBaseUrl)}`, options)
      .pipe(
        map(res => res.data),
        catchError(this._handleError.bind(this)));
  }

  post<T>(endpoint: string, data: any, useAuthHeaders: boolean = true): Observable<T> {
    const options = {
      headers: this._buildHeaders(useAuthHeaders),
    };
    return this.http.post<ShowResponse<T>>(`${this.baseUrl}/${endpoint}`, data, options)
      .pipe(
        map(res => res.data),
        catchError(this._handleError.bind(this)));
  }

  put<T>(endpoint: string, data: any, useAuthHeaders: boolean = true): Observable<T> {
    const options = {
      headers: this._buildHeaders(useAuthHeaders),
    };
    return this.http.put<ShowResponse<T>>(`${this.baseUrl}/${endpoint}`, data, options)
      .pipe(
        map(res => res.data),
        catchError(this._handleError.bind(this)));
  }

  delete<T>(endpoint: string, useAuthHeaders: boolean = true): Observable<T> {
    const options = {
      headers: this._buildHeaders(useAuthHeaders),
    };
    return this.http.delete<ShowResponse<T>>(`${this.baseUrl}/${endpoint}`, options)
      .pipe(
        map(res => res.data),
        catchError(this._handleError.bind(this)));
  }

  private _buildUrl(endpoint: string, useBaseUrl: boolean = false) {
    if (!!useBaseUrl) {
      return `${this.baseUrl}/${endpoint}`;
    }
    return endpoint;
  }

  private _buildHeaders(useAuthHeaders: boolean): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    if (!useAuthHeaders) {
      return httpHeaders;
    }
    httpHeaders = httpHeaders.set('x-auth-token', `${ApiService._getToken()}`);
    return httpHeaders;
  }

  private _handleError(httpErrorResponse: HttpErrorResponse) {
    console.log(httpErrorResponse);
    if (httpErrorResponse.status === 401) {
      ApiService._removeToken();
      this.router.navigateByUrl('/').then();
    }
    const error = httpErrorResponse.error;
    let message = httpErrorResponse.statusText;

    if (!!error.error) {
      message = error.error;
    }

    if (!!error.errors) {
      message = error.errors[0].msg.split(':')[2].toUpperCase();
    }

    if (message === 'Unknown Error' && error instanceof ProgressEvent) {
      message = 'Please check your internet connection!';
    }

    const mpError: MpError = {
      code: error.code,
      detail: message,
      status: httpErrorResponse.status.toString(),
    };
    this.errorService.emit(mpError);
    return throwError({ message, error });
  }
}
